import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { ClientsService } from "../services/clientsService";
import type { ClientByIdResponse } from "../types/clients-api.types";
import { canDeleteClients } from "../utils/clientsPermissions";
import { displayClientName, getTypeBadgeVariant } from "../utils/clientsTableHelpers";
import { DeleteEntityDialogs } from "./DeleteEntityDialogs";
import { EntityDangerZone } from "./EntityDangerZone";
import { KycVerificationSection } from "./kyc/KycVerificationSection";
import { KycStatusPill } from "./KycStatusPill";

export interface ViewClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string | null;
  /** Fired after the client is deleted so the host can refresh its list. */
  onClientDeleted?: () => void;
  /**
   * Hands the client's bank accounts over to the host — the way out when deletion is blocked by them. The
   * host owns both surfaces because the accounts panel is itself a drawer and the two cannot stack. Omit it
   * (as the host does without `client_accounts:read`) and the refusal is stated without an escape hatch.
   */
  onManageAccounts?: (clientId: string, displayName: string) => void;
}

/**
 * Read-only client detail drawer. The entity is ALWAYS a client (`GET /internal/clients/:id`), for every user
 * type.
 *
 * Organizations are NOT rendered here: the master-detail gives them their own page
 * (`/app/clients/organizations/:id`), which owns the organization's KYC and its deletion. This drawer used to
 * branch on `isInternalUser` and call the organization endpoint, which meant an INTERNAL user opening a
 * client row sent a client id to `/internal/clients/organizations/:id` and got a 404. Entity type is decided
 * by the route/table that opens the drawer, never by who is logged in.
 *
 * Editing is not offered here — the clients table's row action opens `EditClientModal`, which renders the
 * same `EditClientForm` over the same payload. One entry point, not two.
 */
export function ViewClientModal({
  isOpen,
  onClose,
  clientId,
  onClientDeleted,
  onManageAccounts,
}: ViewClientModalProps) {
  const { t } = useTranslations("clients");
  const { isInternalUser, user } = useAuth();
  const allowDelete = canDeleteClients(user);

  const [client, setClient] = useState<ClientByIdResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteStep, setDeleteStep] = useState<"first" | "final" | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Id of the LATEST requested detail. Guards against an out-of-order response landing after the consumer
  // swapped `clientId` (quick row switches) or closed the drawer.
  const requestedClientIdRef = useRef<string | null>(null);

  const loadClient = useCallback(async (id: string) => {
    requestedClientIdRef.current = id;
    setLoading(true);
    setError(null);
    try {
      const { data } = await ClientsService.fetchById(id);
      if (requestedClientIdRef.current !== id) return;
      setClient(data);
    } catch (err) {
      if (requestedClientIdRef.current !== id) return;
      setError(HttpApiService.getErrorMessage(err));
      setClient(null);
    } finally {
      if (requestedClientIdRef.current === id) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && clientId) {
      loadClient(clientId);
    } else if (!isOpen) {
      requestedClientIdRef.current = null;
      setClient(null);
      setError(null);
      setDeleteStep(null);
      setDeleting(false);
    }
  }, [isOpen, clientId, loadClient]);

  const displayName = client ? displayClientName(client) : "—";
  // Mock detail-drawer header: initials avatar + name + document (mono) + KYC pill.
  const headerInitials =
    displayName
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase() || "?";
  const headerDocLabel =
    client?.identification_type && client?.identification_number
      ? `${client.identification_type} ${client.identification_number}`
      : null;

  const handleCancelDelete = useCallback(() => {
    if (deleting) return;
    setDeleteStep(null);
  }, [deleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!client) return;
    setDeleting(true);
    try {
      await ClientsService.deleteClient(client.id);
      colocaToast.success(t("view_toast_delete_client_success"));
      setDeleteStep(null);
      onClientDeleted?.();
      // Nothing left to show for a deleted client — the host list refreshes behind the drawer.
      onClose();
    } catch (err) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("view_toast_delete_client_error"));
    } finally {
      setDeleting(false);
    }
  }, [client, onClientDeleted, onClose, t]);

  // The detail payload already carries the client's accounts, so the refusal the backend would answer with
  // (409 while `client_id` still has live rows in `accounts`) is known here without a second request. The
  // backend stays the boundary — this only spares the operator a round trip and names the way out.
  const registeredAccounts = client?.accounts?.length ?? 0;
  const deleteBlockedReason =
    registeredAccounts === 0
      ? null
      : registeredAccounts === 1
        ? t("view_delete_client_blocked_one")
        : t("view_delete_client_blocked_many", { count: registeredAccounts });

  const handleAttemptClose = useCallback(() => {
    // A delete request is in flight — closing now would let its resolution mutate a dismissed view.
    if (deleting) return;
    onClose();
  }, [deleting, onClose]);

  return (
    <>
      <ColocaDrawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleAttemptClose();
        }}
        size="lg"
        title={
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-foreground text-sm font-semibold text-background">
              {headerInitials}
            </span>
            <span className="min-w-0">
              <span className="block truncate">{displayName}</span>
              {headerDocLabel && (
                <span className="mt-0.5 block font-mono text-[11.5px] font-normal text-muted-foreground">
                  {headerDocLabel}
                </span>
              )}
            </span>
            {client && <KycStatusPill status={client.kyc_status} />}
          </span>
        }
      >
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t("view_loading")}</p>
          </div>
        )}

        {error && !loading && (
          <div className="p-4 rounded-lg bg-danger-container text-danger-container-foreground text-sm">
            {t("view_error")}
          </div>
        )}

        {client && !loading && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("view_label_name")}
                </p>
                <p className="mt-0.5 text-sm text-foreground">{displayClientName(client)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("view_label_type")}
                </p>
                <div className="mt-0.5">
                  <ColocaStatusPill variant={getTypeBadgeVariant(client.type)} size="sm">
                    {client.type.charAt(0).toUpperCase() + client.type.slice(1).toLowerCase()}
                  </ColocaStatusPill>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("view_label_email")}
                </p>
                <p className="mt-0.5 text-sm text-foreground">{client.email ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("view_label_country")}
                </p>
                <p className="mt-0.5 text-sm text-foreground">{client.country ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("view_label_phone")}
                </p>
                <p className="mt-0.5 text-sm text-foreground">{client.phone ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("view_label_registration_date")}
                </p>
                <p className="mt-0.5 text-sm text-foreground">
                  {formatDateTime(client.created_at, { includeTime: false }) || "—"}
                </p>
              </div>
            </div>

            <KycVerificationSection client={client} canSendEmail={isInternalUser} />

            {allowDelete && (
              <EntityDangerZone
                variant="client"
                entityName={displayClientName(client)}
                onAction={() => setDeleteStep("first")}
                disabled={deleting}
                blockedReason={deleteBlockedReason}
                blockedAction={
                  deleteBlockedReason && onManageAccounts
                    ? {
                        label: t("view_delete_client_blocked_action"),
                        onClick: () => onManageAccounts(client.id, displayClientName(client)),
                      }
                    : undefined
                }
              />
            )}
          </div>
        )}

        {!client && !loading && !error && isOpen && clientId && (
          <div className="py-8 text-center text-sm text-muted-foreground">{t("view_loading")}</div>
        )}
        {/* Sentinel: with a single body child, ColocaDrawer sticky-pins its last child (a helper meant for
            footer-less wizards). This detail view manages its own layout, so force the normal flow. */}
        <span aria-hidden className="hidden" />
      </ColocaDrawer>

      <DeleteEntityDialogs
        entity={client}
        variant="client"
        step={deleteStep}
        deleting={deleting}
        onCancel={handleCancelDelete}
        onProceed={() => setDeleteStep("final")}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
